import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { TOOLTIP_IDS, tooltipStore } from '../../onboarding';
import {
  type Trigger,
  useKeyboardShortcutsTracking,
} from '../hooks/useKeyboardShortcutsTracking';
import { CommandRegistryHelpDialog } from './CommandRegistryHelpDialog';
import { KeyboardShortcut } from './KeyboardShortcut';

export const CommandRegistryHelpDialogContext = createContext({
  isKeyboardShortcutsHelpDialogOpen: false,
  toggleKeyboardShortcutsHelpDialogOpen: noop,
});

type CommandRegistryHelpDialogContextProviderProps = PropsWithChildren<{
  isEnabled?: boolean;
  isShareContext?: boolean;
}>;

/**
 * Provider the for the keyboard shortcut help dialog. Must be a child of
 * `<CommandRegistryContextProvider />`.
 */
export function CommandRegistryHelpDialogContextProvider({
  isEnabled = true,
  isShareContext,
  children,
}: CommandRegistryHelpDialogContextProviderProps) {
  const [
    isKeyboardShortcutsHelpDialogOpen,
    setIsKeyboardShortcutsHelpDialogOpen,
  ] = useState(false);
  const { trackDialogOpened } = useKeyboardShortcutsTracking();

  const toggleKeyboardShortcutsHelpDialogOpen = useCallback(
    (trigger?: Trigger) => {
      setIsKeyboardShortcutsHelpDialogOpen((prev) => {
        const next = !prev;
        if (next) {
          if (trigger) {
            trackDialogOpened(trigger);
          }
          // Request the custom-shortcuts discovery tooltip. We pass
          // `bypassEligibility: true` because the store's default visit-count
          // gate (`visitCount >= showAfterVisitNumber`) never opens for
          // `manualOpenOnly` tooltips — they're not auto-enqueued by route
          // navigation, so `visitCount` stays at 0. Bypassing skips that gate
          // but still respects the `customShortcuts` feature-flag eligibility
          // and the persisted dismissal state, which is what we actually want.
          tooltipStore.openTooltip(TOOLTIP_IDS.CUSTOM_SHORTCUTS_CAPTURE, {
            bypassEligibility: true,
          });
        }
        return next;
      });
    },
    [trackDialogOpened]
  );

  const value = useMemo(
    () => ({
      isKeyboardShortcutsHelpDialogOpen,
      toggleKeyboardShortcutsHelpDialogOpen,
    }),
    [isKeyboardShortcutsHelpDialogOpen, toggleKeyboardShortcutsHelpDialogOpen]
  );

  return (
    <CommandRegistryHelpDialogContext.Provider value={value}>
      {isEnabled && (
        <KeyboardShortcut
          id="global.help"
          onKeyDown={() =>
            toggleKeyboardShortcutsHelpDialogOpen('HelpShortcut')
          }
        />
      )}

      {children}

      <CommandRegistryHelpDialog
        isOpen={isKeyboardShortcutsHelpDialogOpen}
        isShareContext={isShareContext}
        onRequestClose={() => toggleKeyboardShortcutsHelpDialogOpen()}
      />
    </CommandRegistryHelpDialogContext.Provider>
  );
}
